import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import { getStoredAllUserData } from '../services/authService';
import { useAuth } from './AuthContext';

export interface ChatMessage {
  id: string;
  oduserId: string;
  userName: string;
  userLevel: number;
  message: string;
  timestamp: string;
  avatar?: string;
}

interface LiveChatContextType {
  isOpen: boolean;
  messages: ChatMessage[];
  onlineCount: number;
  isConnected: boolean;
  chatError: string | null;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  sendMessage: (message: string) => void;
  clearError: () => void;
}

const LiveChatContext = createContext<LiveChatContextType | undefined>(undefined);

let globalSocket: ReturnType<typeof io> | null = null;

function getSocketUrl() {
  return (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001')
    .replace(/\/api\/?$/, '')
    .replace(/\/$/, '');
}

function ensureSocket(): ReturnType<typeof io> {
  if (globalSocket) return globalSocket;

  globalSocket = io(getSocketUrl(), {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 15,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 10000,
  });

  return globalSocket;
}

export const LiveChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user: authUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  useEffect(() => {
    const socket = ensureSocket();

    const joinRoom = () => {
      socket.emit('join-live-chat');
    };

    const onConnect = () => {
      setIsConnected(true);
      joinRoom();
    };

    const onDisconnect = () => {
      setIsConnected(false);
    };

    const onConnectError = (error: Error) => {
      console.error('[LiveChat] Connection error:', error.message);
      setIsConnected(false);
    };

    const onMessage = (message: ChatMessage) => {
      setMessages((prev) => [...prev, message].slice(-100));
    };

    const onHistory = (history: ChatMessage[]) => {
      if (Array.isArray(history)) {
        setMessages(history.slice(-100));
      }
    };

    const onOnlineCount = (count: number) => {
      setOnlineCount(count);
    };

    const onChatError = (data: { error: string }) => {
      setChatError(data.error);
      setTimeout(() => setChatError(null), 3000);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('live-chat-message', onMessage);
    socket.on('live-chat-history', onHistory);
    socket.on('live-chat-online-count', onOnlineCount);
    socket.on('live-chat-error', onChatError);

    if (socket.connected) {
      setIsConnected(true);
      joinRoom();
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('live-chat-message', onMessage);
      socket.off('live-chat-history', onHistory);
      socket.off('live-chat-online-count', onOnlineCount);
      socket.off('live-chat-error', onChatError);
    };
  }, []);

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const clearError = useCallback(() => {
    setChatError(null);
  }, []);

  const sendMessage = useCallback((message: string) => {
    if (!message.trim()) return;

    const socket = ensureSocket();

    if (!socket.connected) {
      socket.connect();
      setChatError('Connection lost. Reconnecting... Please try again in a moment.');
      setTimeout(() => setChatError(null), 4000);
      return;
    }

    const userId = authUser?.id;
    if (!userId) {
      setChatError('Please log in to send messages.');
      setTimeout(() => setChatError(null), 3000);
      return;
    }

    const userData = getStoredAllUserData();
    const user = userData?.user;
    const userName = authUser?.name ?? user?.name ?? (user as { loginId?: string })?.loginId ?? 'Anonymous';
    const userLevel = authUser?.level ?? (user as { level?: number })?.level ?? 1;
    const avatar = (user as { avatar?: string | null })?.avatar ?? null;

    socket.emit('send-live-chat-message', {
      oduserId: userId,
      userId,
      userName,
      userLevel,
      message: message.trim(),
      avatar,
    });
  }, [authUser?.id, authUser?.name, authUser?.level]);

  return (
    <LiveChatContext.Provider
      value={{
        isOpen,
        messages,
        onlineCount,
        isConnected,
        chatError,
        toggleChat,
        openChat,
        closeChat,
        sendMessage,
        clearError,
      }}
    >
      {children}
    </LiveChatContext.Provider>
  );
};

export const useLiveChat = (): LiveChatContextType => {
  const context = useContext(LiveChatContext);
  if (!context) {
    throw new Error('useLiveChat must be used within a LiveChatProvider');
  }
  return context;
};

export default LiveChatContext;
